import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { AuthModal } from '../components/AuthModal'
import { Button } from '../components/ui/button'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import {
  ArrowRight,
  Building2,
  Globe,
  ShieldCheck,
  Users,
  FileCheck,
  Layers,
  Rocket,
  ChevronDown,
  BadgeCheck,
  TrendingUp,
  Banknote,
  Network,
  Gem,
  BarChart3,
  Landmark,
  PieChart,
  Zap
} from 'lucide-react'

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }

export default function OwnersPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { tenant } = useTenant()
  const [authOpen, setAuthOpen] = useState(false)

  const steps = [
    {
      num: '01',
      icon: FileCheck,
      title: t('owners.steps.consult.title'),
      description: t('owners.steps.consult.description'),
    },
    {
      num: '02',
      icon: Layers,
      title: t('owners.steps.structure.title'),
      description: t('owners.steps.structure.description'),
    },
    {
      num: '03',
      icon: Rocket,
      title: t('owners.steps.launch.title'),
      description: t('owners.steps.launch.description'),
    },
  ]

  const benefits = [
    { icon: Users, title: t('owners.benefits.reach.title'), description: t('owners.benefits.reach.description') },
    { icon: Banknote, title: t('owners.benefits.capital.title'), description: t('owners.benefits.capital.description') },
    { icon: PieChart, title: t('owners.benefits.fractional.title'), description: t('owners.benefits.fractional.description') },
    { icon: Zap, title: t('owners.benefits.speed.title'), description: t('owners.benefits.speed.description') },
    { icon: ShieldCheck, title: t('owners.benefits.compliant.title'), description: t('owners.benefits.compliant.description') },
    { icon: Network, title: t('owners.benefits.liquidity.title'), description: t('owners.benefits.liquidity.description') },
  ]

  const assetTypes = [
    { icon: Building2, label: t('owners.assets.realEstate'), desc: t('owners.assets.realEstateDesc') },
    { icon: Gem, label: t('owners.assets.commodities'), desc: t('owners.assets.commoditiesDesc') },
    { icon: Landmark, label: t('owners.assets.funds'), desc: t('owners.assets.fundsDesc') },
    { icon: BarChart3, label: t('owners.assets.equity'), desc: t('owners.assets.equityDesc') },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center bg-brand-secondary overflow-hidden" data-testid="owners-hero">
        <div className="absolute inset-0 hero-bg-kenburns">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80&auto=format"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-secondary/90 via-brand-secondary/75 to-brand-secondary/95" />
        <div className="absolute inset-0 hero-radial-glow" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.span variants={fadeUp}
                className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-8 backdrop-blur-sm">
                {t('owners.hero.badge')}
              </motion.span>
              <motion.h1 variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-6xl font-heading text-white leading-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
                {t('owners.hero.title1')}{' '}
                <span className="text-brand-primary hero-text-glow">{t('owners.hero.title2')}</span>
              </motion.h1>
              <motion.div variants={fadeUp} className="w-16 h-px bg-brand-primary my-8 shadow-[0_0_12px_rgba(var(--brand-primary-rgb)/0.6)]" />
              <motion.p variants={fadeUp} className="text-lg text-white/70 font-body drop-shadow-md max-w-lg">
                {t('owners.hero.subtitle')}
              </motion.p>
              <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button onClick={() => isAuthenticated ? navigate('/issuer') : setAuthOpen(true)}
                  className="h-16 px-10 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide text-base rounded-none shadow-[0_0_30px_rgba(var(--brand-primary-rgb)/0.3)]"
                  data-testid="owners-hero-cta">
                  <Building2 className="w-5 h-5 mr-2" />{t('owners.hero.cta')}<ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </motion.div>

            {/* Key metrics */}
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
              className="hidden lg:block">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 space-y-6">
                {[
                  { label: t('owners.hero.metric1'), value: t('owners.hero.metric1val') },
                  { label: t('owners.hero.metric2'), value: t('owners.hero.metric2val') },
                  { label: t('owners.hero.metric3'), value: t('owners.hero.metric3val') },
                ].map((m, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-white/10 pb-4 last:border-0 last:pb-0">
                    <span className="text-sm text-white/60 uppercase tracking-wider">{m.label}</span>
                    <span className="text-xl font-heading text-brand-primary">{m.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="owners-value">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="relative order-2 lg:order-1">
              <div className="bg-brand-secondary p-8 lg:p-10">
                <Globe className="w-12 h-12 text-brand-primary mb-6" />
                <h3 className="text-xl font-heading text-white mb-4">{t('owners.value.cardTitle')}</h3>
                <div className="space-y-4">
                  {[
                    t('owners.value.point1'),
                    t('owners.value.point2'),
                    t('owners.value.point3'),
                  ].map((text, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <BadgeCheck className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
                      <p className="text-white/80 text-sm leading-relaxed">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-full h-full border border-brand-primary/20 -z-10" />
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="order-1 lg:order-2">
              <motion.span variants={fadeUp}
                className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-6">
                {t('owners.value.badge')}
              </motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-heading text-brand-secondary mb-6">
                {t('owners.value.title')}
              </motion.h2>
              <motion.div variants={fadeUp} className="w-16 h-px bg-brand-primary mb-6" />
              <motion.p variants={fadeUp} className="text-slate-600 leading-relaxed mb-4">
                {t('owners.value.p1')}
              </motion.p>
              <motion.p variants={fadeUp} className="text-slate-600 leading-relaxed">
                {t('owners.value.p2')}
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-white" data-testid="owners-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('owners.steps.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('owners.steps.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">{t('owners.steps.subtitle')}</p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-primary/30 to-transparent -translate-y-1/2" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }} viewport={{ once: true }}
                  className="relative bg-white border border-slate-100 p-8 hover:border-brand-primary/30 transition-all duration-300 group"
                  data-testid={`owners-step-${index + 1}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-5xl font-heading text-brand-primary/20 group-hover:text-brand-primary/40 transition-colors">{step.num}</span>
                    <div className="w-14 h-14 bg-brand-primary/10 flex items-center justify-center group-hover:bg-brand-primary/20 transition-colors">
                      <step.icon className="w-7 h-7 text-brand-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-heading text-brand-secondary mb-3">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="owners-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('owners.benefits.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('owners.benefits.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }} viewport={{ once: true }}
                className="p-6 bg-white border border-slate-100 hover:border-brand-primary/30 transition-all duration-300 group"
                data-testid={`owners-benefit-${index}`}>
                <div className="w-14 h-14 bg-brand-primary/10 flex items-center justify-center mb-4 group-hover:bg-brand-primary/20 transition-colors">
                  <b.icon className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="text-lg font-heading text-brand-secondary mb-2">{b.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{b.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Asset Types */}
      <section className="py-24 bg-brand-secondary" data-testid="owners-assets">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('owners.assets.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-white">{t('owners.assets.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
            <p className="text-white/60 mt-4 max-w-xl mx-auto">{t('owners.assets.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {assetTypes.map((asset, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                className="p-6 border border-white/10 bg-white/5 backdrop-blur-sm hover:border-brand-primary/30 transition-all group text-center"
                data-testid={`owners-asset-${i}`}>
                <asset.icon className="w-10 h-10 text-brand-primary mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-base font-heading text-white mb-2">{asset.label}</h3>
                <p className="text-xs text-white/60 leading-relaxed">{asset.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white" data-testid="owners-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
            {t('owners.cta.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary mb-4">{t('owners.cta.title')}</h2>
          <div className="w-16 h-px bg-brand-primary mx-auto my-6" />
          <p className="text-slate-500 max-w-xl mx-auto mb-10">{t('owners.cta.subtitle')}</p>
          <Button onClick={() => isAuthenticated ? navigate('/issuer') : setAuthOpen(true)}
            className="h-16 px-10 bg-brand-secondary hover:bg-brand-secondary/90 text-white font-semibold tracking-wide rounded-none"
            data-testid="owners-cta-btn">
            <Building2 className="w-5 h-5 mr-2" />{t('owners.cta.button')}<ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-brand-secondary border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={tenant?.branding?.logo_url || "https://uni2ecosystem.com/images/logos/tokenizelogo.png"} alt={tenant?.name || "2kenize"} className="h-8 w-auto" />
            <p className="text-sm text-white/50">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  )
}
